import { PencilLine, Sparkles, ShieldCheck } from "lucide-react";
import { indianState } from "@flint/api-contract";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { paiseToInrShort } from "../../../lib/format.js";
import type { VendorBillSummary, VendorOption } from "./types.js";
import { Field, SectionCard } from "./section-card.js";

interface VendorSectionProps {
  vendors: VendorOption[];
  vendorId: string;
  placeOfSupply: string;
  category: string;
  currency: string;
  summary: VendorBillSummary | null;
  /** Company state name — drives the "- same state" marker in the
   *  place-of-supply dropdown. Was hardcoded to "Maharashtra" until
   *  the bill-composer was rewired to read company.state at runtime. */
  companyState: string;
  onVendorChange: (id: string) => void;
  onFieldChange: (field: "placeOfSupply" | "category" | "currency", value: string) => void;
  error?: string | undefined;
}

export function VendorSection({
  vendors,
  vendorId,
  placeOfSupply,
  category,
  currency,
  summary,
  companyState,
  onVendorChange,
  onFieldChange,
  error,
}: VendorSectionProps) {
  const vendor = vendors.find((item) => item.id === vendorId) ?? null;
  const stateCode = vendor?.gstin?.slice(0, 2) || stateCodeFor(vendor?.state);
  const address = formatVendorAddress(vendor);
  const contact = formatVendorContact(vendor);

  return (
    <SectionCard
      step="1"
      title="Vendor"
      description="Who issued this bill. GSTIN drives the tax mode (intra-state vs. inter-state)."
      action={
        <button
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-sm border border-sky-100 bg-sky-50 px-3 text-xs font-semibold text-sky-700"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Suggest vendor
        </button>
      }
    >
      <div className="grid gap-4">
        {vendor ? (
          <div className="grid gap-3 rounded-md border border-line bg-raised p-3 md:grid-cols-[auto_1fr_auto]">
            <div className="grid h-12 w-12 place-items-center rounded-md bg-ink text-base font-semibold text-white">
              {vendorInitials(vendor.name)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-semibold text-ink">{vendor.name}</p>
                {vendor.gstin ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-2 py-1 text-xs font-medium text-ok">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    GSTIN verified
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted">
                <strong className="font-mono text-ink-2">
                  {vendor.gstin ?? "GSTIN not on file"}
                </strong>
                {vendor.state ? ` - ${vendor.state}${stateCode ? ` (${stateCode})` : ""}` : ""}
                {address ? ` - ${address}` : ""}
              </p>
              <p className="mt-1 text-xs text-muted">{contact}</p>
            </div>
            <div className="flex items-start gap-2">
              <Select value={vendorId} onValueChange={onVendorChange}>
                <SelectTrigger className="h-8 w-44 min-w-0 overflow-hidden text-xs">
                  <SelectValue placeholder="Change" className="block min-w-0 truncate" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((item) => (
                    <SelectItem key={item.id} value={item.id} className="max-w-72">
                      <span className="block truncate">{item.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PencilLine className="mt-2 hidden h-4 w-4 text-muted md:block" />
            </div>
          </div>
        ) : (
          <Field label="Vendor" required error={error}>
            <Select value={vendorId} onValueChange={onVendorChange}>
              <SelectTrigger className="min-w-0 overflow-hidden">
                <SelectValue placeholder="Select vendor" className="block min-w-0 truncate" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((item) => (
                  <SelectItem key={item.id} value={item.id} className="max-w-72">
                    <span className="block truncate">{item.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        <div className="grid overflow-hidden rounded-md border border-line bg-line md:grid-cols-3">
          {[
            ["Bills with us", billsWithUs(summary), "text-ink"],
            [
              "Payment record",
              paymentRecord(summary),
              summary?.paidBillCount ? "text-ok" : "text-muted",
            ],
            ["Last bill", lastBill(summary), "text-ink"],
          ].map(([label, value, tone]) => (
            <div key={label} className="bg-paper p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
              <p className={`mt-1 font-mono text-sm font-semibold ${tone}`}>{value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Place of supply">
            <Select
              value={placeOfSupply}
              onValueChange={(value) => onFieldChange("placeOfSupply", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {indianState.options.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                    {stateCodeFor(state) ? ` (${stateCodeFor(state)})` : ""}
                    {state === companyState ? " - same state" : " - IGST"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">Auto-derived from vendor GSTIN</p>
          </Field>
          <Field label="Vendor category">
            <Select value={category} onValueChange={(value) => onFieldChange("category", value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Raw material - fasteners">Raw material - fasteners</SelectItem>
                <SelectItem value="Raw material - general">Raw material - general</SelectItem>
                <SelectItem value="Services">Services</SelectItem>
                <SelectItem value="Capital goods">Capital goods</SelectItem>
                <SelectItem value="Office supplies">Office supplies</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">Drives the default GL account</p>
          </Field>
          <Field label="Currency">
            <Select value={currency} onValueChange={(value) => onFieldChange("currency", value)}>
              <SelectTrigger disabled>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR - Indian Rupee</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">V1 supports INR only</p>
          </Field>
        </div>
      </div>
    </SectionCard>
  );
}

function vendorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "")).toUpperCase();
}

function formatVendorAddress(vendor: VendorOption | null): string {
  if (!vendor) return "";
  return [vendor.addressLine1, vendor.addressLine2, vendor.city, vendor.pincode]
    .filter(Boolean)
    .join(", ");
}

function formatVendorContact(vendor: VendorOption | null): string {
  if (!vendor) return "Select a vendor to load contact details.";
  const name = vendor.contactName
    ? `Account mgr ${vendor.contactName}`
    : "Account contact not on file";
  const email = vendor.email ?? "email not on file";
  const phone = vendor.phone ?? "phone not on file";
  return `${name} - ${email} - ${phone}`;
}

function billsWithUs(summary: VendorBillSummary | null): string {
  if (!summary || summary.billCount === 0) return "No bills yet";
  return `${summary.billCount} - since ${formatMonthYear(summary.firstBillOn)}`;
}

function paymentRecord(summary: VendorBillSummary | null): string {
  if (!summary || summary.billCount === 0) return "No payment history";
  if (summary.paidBillCount === 0) return "No paid bills yet";
  return `${summary.paidBillCount}/${summary.billCount} paid bills`;
}

function lastBill(summary: VendorBillSummary | null): string {
  if (!summary?.lastBillNumber || !summary.lastBillTotalPaise) return "No previous bill";
  const amount = paiseToInrShort(BigInt(summary.lastBillTotalPaise));
  const date = formatShortDate(summary.lastBillOn);
  return `${summary.lastBillNumber} - ${amount}${date ? ` (${date})` : ""}`;
}

function formatMonthYear(iso: string | null): string {
  if (!iso) return "unknown";
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString("en-IN", { month: "short", year: "numeric", timeZone: "UTC" });
}

function formatShortDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });
}

function stateCodeFor(state: string | null | undefined): string {
  const codes: Record<string, string> = {
    Gujarat: "24",
    Maharashtra: "27",
    "Tamil Nadu": "33",
  };
  return state ? (codes[state] ?? "") : "";
}
